import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { OnboardingProductsStore } from '../../../store/store';
import {
  FinancialDataManagerCreateActions,
  UiActions
} from '../../../store/actions';
import { DataNotClientService } from '../../../services/data-not-client.service';
import { LoadingService } from '../../../services/loading.service';
import { SessionService } from '../../../services/session.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ApiResponseStatus, StepsClientTypes, StepsNotClientTypes } from '../../../shared/enums';
import { PepComponent } from 'omnichannel2-commercial-platform-pep';
import { RedirectService } from 'src/app/services/redirect.service';
import { environment } from 'src/environments/environment';
import { DatadogService } from 'src/app/services/datadog/datadog.service';
import { RequestFinancialDataManagerCreate } from '../../../shared/interfaces';
import { DataClientService } from 'src/app/services/data-client.service';
import { TagPage } from 'src/app/store/actions/ui.actions';

@Component({
  selector: 'lib-pep-form',
  templateUrl: './pep-form.component.html',
  styleUrls: [
    '../../../global.scss', './pep-form.component.scss'
  ],
  encapsulation: ViewEncapsulation.None
})

export class PepFormComponent implements OnInit, OnDestroy {
  private isWC = environment.isWc;
  staticAssetsUrl = environment.staticScripts.staticAssets;
  expandedIndex: number | undefined;
  maskPep = false;
  pepCont = 0;
  isExecutive: boolean;
  isMarkByCIGO: boolean;
  pepMarkByCigo: boolean;
  showFisherman: boolean;
  varIsPepActive: boolean;
  isJointAccount: boolean;
  formData: FormGroup;
  privateMode: boolean;
  dataService: DataNotClientService | DataClientService;

  @ViewChild(PepComponent, { static: false }) pep: PepComponent;
  invalidPep = false; // el invalidPep siempre se inicia con false, significa que el formulario es valido si nunca se modifica su estado

  SwitchData = [
    {
      title: '<strong>Eres una Persona Expuesta Pública y/o Políticamente (PEP)?</strong>',
      name: 'isPEP',
      description: 'Personas expuestas políticamente (PEP) son personas naturales, nacionales o extranjeras, '
      + 'que cumplen o que en los últimos cinco (5) años hayan cumplido funciones públicas destacadas o funciones '
      + 'prominentes en una organización internacional, sea en territorio nacional o extranjero, y cuyas circunstancias '
      + 'financieras puedan ser objeto de un interés. Asimismo, se considera como PEP al colaborador directo de la máxima autoridad de la institución.'
    },
    {
      title: '<strong>¿Eres una persona que aplica a FATCA?</strong>',
      name: 'isFATCA',
      description: 'FATCA (Foreign Account Tax Compilance Act) es una normativa que tiene como objetivo promover '
      + 'la transparencia fiscal y el cumplimiento de las obligaciones fiscales de los ciudadanos y residentes estadounidenses.'
    },
    {
      title: '<strong>¿Eres una persona que aplica a ECR?</strong>',
      name: 'isECR',
      description: 'Estandar Común de Reporte o Common Reporting Standard (ECR), establece la calidad de información financiera '
      + 'que se intercambiará, las instituciones financieras sujetas a reportar dicha información, los distintos tipos de cuentas, los contribuyentes '
      + 'reportables, así como los procedimientos de debida diligencia que las instituciones financieras deben seguir. Aplica para extranjeros no estadounidenses.'
    }
  ];

  financialDataRequestManager$ = this.store.select(OnboardingProductsStore.selectors.financialDataManagerCreate);

  constructor(
    private store: Store<OnboardingProductsStore.AppState>,
    private dataNotClientService: DataNotClientService,
    private dataClientService: DataClientService,
    private dataDogService: DatadogService,
    public loadingService: LoadingService,
    public sessionService: SessionService,
    private _fb: FormBuilder,
    private redirectService: RedirectService
  ) {
    this.formData = this._fb.group({
      isPEP: [false],
      isFATCA: [false],
      isECR: [false],
      isFisherman: [false]
    });
  }

  financialDataRequestManagerSuscription = this.financialDataRequestManager$.subscribe(({ response, error }) => {
    if (error != null) {
      const step = this.privateMode ? StepsClientTypes.STEP_SERVICE_ERROR : StepsNotClientTypes.STEP_SERVICE_ERROR;
      this.store.dispatch(FinancialDataManagerCreateActions.cleanState());
      this.store.dispatch(UiActions.Navigate({ step }));
    }

    if (response) {
      this.store.dispatch(FinancialDataManagerCreateActions.cleanState());
      if (response.client.operationState.code === ApiResponseStatus.OK) {
        if (this.formData.controls.isPEP.value || this.formData.controls.isECR.value || this.formData.controls.isFATCA.value) {
          if (environment.mode === 'WEB-PF') {
            const redirect = this.privateMode ? StepsClientTypes.STEP_CURRENCY_TYPE : StepsNotClientTypes.STEP_CURRENCY_TYPE;
            this.store.dispatch(UiActions.ToggleModal({
              text: 'No te olvides de imprimir el Formulario PEP, FATCA o ECR y completarlo con los datos del cliente.',
              buttonText: 'Continuar',
              icon: 'print',
              open: true,
              redirect
            }));
          } else if (environment.mode === 'PF') {
            return this.store.dispatch(UiActions.Navigate({step: StepsClientTypes.STEP_CURRENCY_TYPE}));
          } else {
            const redirect = this.privateMode ? StepsClientTypes.STEP_SUCURSAL_DERIVATION : StepsNotClientTypes.STEP_SUCURSAL_DERIVATION;
            return this.store.dispatch(UiActions.Navigate({step: redirect}));
          }
        } else {
          const redirect = StepsNotClientTypes.STEP_CURRENCY_TYPE;
          return this.store.dispatch(UiActions.Navigate({ step: redirect }));
        }
      } else {
        const redirect = this.privateMode ? StepsClientTypes.STEP_SERVICE_ERROR : StepsNotClientTypes.STEP_SERVICE_ERROR;
        return this.store.dispatch(UiActions.Navigate({step: redirect}));
      }
    }
  });

  ngOnInit() {
    this.setService();
    this.checkFisherman();
    this.checkNationality();
    this.checkCigoData();
    setTimeout(() => {
      this.loadingService.loading = false;
    }, 100);
  }

  ngOnDestroy() {
    this.financialDataRequestManagerSuscription.unsubscribe();
  }

  setService() {
    this.privateMode = (environment.mode === 'IN' || environment.mode === 'PF') ? true : false;
    this.isExecutive = environment.mode === 'WEB-PF' || environment.mode === 'PF' ? true : false;
    this.dataService = this.privateMode ? this.dataClientService : this.dataNotClientService;
    const googleAnalytics = {
      is_joint_account: this.dataService.isIsJointAccount(),
      job_information: {
        workActivityType: this.dataService.getWorkActivityType() ? this.dataService.getWorkActivityType() : 'No informado',
        dateEntryWork: this.dataService.getDateEntryWork() ? this.dataService.getDateEntryWork() : 'No informado',
        companyName: this.dataService.getCompanyName() ? this.dataService.getCompanyName() : 'No informado',
        rucCompany: this.dataService.getRucCompany() ? this.dataService.getRucCompany() : 'No informado',
        profession: this.dataService.getProfession() ? this.dataService.getProfession() : 'No informado'
      }
    };
    this.store.dispatch(TagPage({text: 'pep-fatca-ecr-information', extras: googleAnalytics}));
  }

  continue() {
    this.dataService.setIsPEP(this.formData.controls.isPEP.value);
    this.dataService.setIsECR(this.formData.controls.isECR.value);
    this.dataService.setIsFATCA(this.formData.controls.isFATCA.value);
    this.dataService.setIsFisherman(this.formData.controls.isFisherman.value);

    const channel = this.privateMode ? 'client' : 'not client';
    this.dataDogService.addCustomEvent('Personal information completed', environment.mode, { channel });

    this.privateMode ? this.continueInClientMode() : this.continueInNotClientMode();
  }

  continueInNotClientMode() {
    if ((this.formData.controls.isPEP.value || this.formData.controls.isECR.value || this.formData.controls.isFATCA.value) && environment.mode === 'WEB') {
      return this.store.dispatch(UiActions.Navigate({step: StepsNotClientTypes.STEP_SUCURSAL_DERIVATION}));
    }
    this.dataService.isIsJointAccount() ? this.redirectPEPJoint(StepsNotClientTypes.STEP_INFORMATION_PEP_JOINT) : this.financialDataDispatch();
  }

  continueInClientMode() {
    if (this.dataService.isIsJointAccount()) {
      this.redirectPEPJoint(StepsClientTypes.STEP_INFORMATION_PEP_JOINT);
    } else if (this.formData.controls.isFATCA.value === true || this.formData.controls.isECR.value === true) {
      if (environment.mode !== 'PF') {
        this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_SUCURSAL_DERIVATION }));
      } else {
        this.financialDataDispatch();
      }
    } else {
      this.financialDataDispatch();
    }
  }

  redirectPEPJoint(redirect) {
    if (this.formData.controls.isPEP.value || this.formData.controls.isECR.value || this.formData.controls.isFATCA.value) {
      return this.store.dispatch(UiActions.ToggleModal({
        text: 'No te olvides de imprimir el Formulario PEP, FATCA o ECR y completarlo con los datos del cliente.',
        buttonText: 'Continuar',
        icon: 'print',
        open: true,
        redirect
      }));
    } else {
      return this.store.dispatch(UiActions.Navigate({step: redirect}));
    }
  }

  financialDataDispatch() {
    this.loadingService.loading = true;
    const request: RequestFinancialDataManagerCreate = {
      client: {
        pep: this.dataService.getIsPEP(),
        ecr: this.dataService.getIsECR(),
        fatca: this.dataService.getIsFATCA(),
        trabajadorPesquero: this.dataService.getIsFisherman()
      }
    };

    this.store.dispatch(FinancialDataManagerCreateActions.getFinancialDataManagerCreate({ request }));
  }

  maskPepButton(val) {
    if (this.formData.controls.isPEP.value === true && this.pepCont === 1 && val === false) {
      this.maskPep = true;
    } else {
      this.maskPep = false;
    }
  }

  checkPep(val?: boolean) {
    this.maskPepButton(val);
    if (this.formData.controls.isPEP.value === true) {
      this.pepCont++;
      this.invalidPep = val !== true;
    } else {
      this.invalidPep = false;
      this.pepCont = 0;
    }
  }

  private checkNationality() {
    const nationality: string = this.dataService.getNationality();
    if (nationality === '4016') {
      this.formData.controls.isFATCA.setValue(true);
      this.formData.controls.isFATCA.disable();
      this.formData.controls.isECR.disable();
    } else if (nationality !== '4028') {
      this.formData.controls.isECR.setValue(true);
      this.formData.controls.isECR.disable();
      this.formData.controls.isFATCA.disable();
    }
  }

  checkCigoData() {
    if (this.dataNotClientService.getMainIsClient() || this.privateMode) {

      const cigo = this.dataService.getCigoData();
      if (cigo.informacionFinancieraCliente) {
        if (cigo.informacionFinancieraCliente.PEP && cigo.informacionFinancieraCliente.PEP === 1) {
          this.pepMarkByCigo = true;
          this.formData.controls.isPEP.setValue(true);
          this.formData.controls.isPEP.disable();
        }
        if (cigo.informacionFinancieraCliente.FATCA &&
          cigo.informacionFinancieraCliente.FATCA.indicadorFatca === 'true'
        ) {
          this.formData.controls.isFATCA.setValue(true);
          this.formData.controls.isFATCA.disable();
          this.formData.controls.isECR.disable();
          this.isMarkByCIGO = true;
        }
      }
    }
  }

  disableFields() {
    if (this.formData.controls.isFATCA.value === true) {
      this.formData.controls.isECR.disable();
    }
    if (this.formData.controls.isFATCA.value === false) {
      this.formData.controls.isECR.enable();
    }
    if (this.formData.controls.isECR.value === true) {
      this.formData.controls.isFATCA.disable();
    }
    if (this.formData.controls.isECR.value === false) {
      this.formData.controls.isFATCA.enable();
    }
  }

  sliderHandler(index) {
    index === 0 ? this.checkPep() : this.disableFields();
    this.expandIndex(index, true);
  }

  checkFisherman() {
    this.showFisherman = this.dataService.getAccountType() === 'CTS';
  }

  expandIndex(i, slider?) {
    this.expandedIndex = i === this.expandedIndex ? slider ? i : undefined : i;
  }

  getIsWC(): boolean {
    const isWc = !!this.isWC;
    return isWc;
  }
}
